<div class="page-wrap mx-auto py-4 py-xl-5">
    @if (loading) {
        <ngxh6-loading-spinner></ngxh6-loading-spinner>
    } @else if (notFound) {
        <h1 class="mt-0">Page not found</h1>
        <p class="text-muted">Sorry, that page doesn't exist. <a routerLink="/">Back to home</a>.</p>
    } @else if (page) {
        <!-- Flow-aware back link (not browser history): a system info page knows the step it
             came from - the recreation pages return to the splitter, the rest to /apply. -->
        @if (backLink) {
            <a class="btn btn-sm btn-link ps-0 mb-2" [routerLink]="backLink.route">
                <i class="fa fa-arrow-left me-1" aria-hidden="true"></i> {{ backLink.label }}
            </a>
        }
        <h1 class="mt-0">{{ page.title }}</h1>
        <!-- Staff-authored rich text; Angular sanitises it on the way in. -->
        <div class="page-body" [innerHTML]="page.body"></div>

        <!-- Dynamic site list on the two recreation info pages. Each card shows its photo
             when there is one and a tinted placeholder when there isn't, so the grid reads
             the same either way. -->
        @if (recreationSites.length) {
            <div class="rec-sites mt-4">
                <div class="row g-3">
                    @for (site of recreationSites; track site.name) {
                        <div class="col-sm-6 col-lg-4">
                            <div class="rec-site card h-100">
                                @if (site.photos.length) {
                                    <img class="rec-site__img" [src]="site.photos[0].url" [alt]="site.photos[0].caption || site.name" loading="lazy" />
                                } @else {
                                    <div class="rec-site__placeholder"><i class="fa {{ sitePlaceholderIcon }}" aria-hidden="true"></i></div>
                                }
                                <div class="card-body">
                                    <h3 class="h6 card-title mb-1">{{ site.name }}</h3>
                                    <p class="card-text small text-muted mb-0">{{ siteFacts(site) }}</p>
                                    <!-- Exclusive-use sites are booked one party at a time - flagged as a
                                         selling point ("the whole site is yours") rather than a capacity. -->
                                    @if (site.exclusiveUse) {
                                        <span class="badge rounded-pill bg-success-subtle text-success-emphasis border border-success-subtle mt-2">
                                            <i class="fa fa-star me-1" aria-hidden="true"></i>Exclusive use — the whole site is yours
                                        </span>
                                    }
                                    @if (site.photos[0]?.caption) {
                                        <p class="card-text small text-muted fst-italic mt-1 mb-0">{{ site.photos[0].caption }}</p>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        }

        <!-- The "Apply" button(s) on a system info page, straight to the application form -
             no extra step between reading and applying. Fishing/Hunting carry two
             (14-day / Annual); everything else one. -->
        @if (applyTargets.length) {
            <div class="page-apply d-flex flex-wrap gap-2 mt-4">
                @for (target of applyTargets; track $index) {
                    <a class="btn btn-primary btn-lg" [routerLink]="target.route" [queryParams]="target.query || {}">
                        {{ target.label }}<i class="fa fa-arrow-right ms-2" aria-hidden="true"></i>
                    </a>
                }
            </div>
        }

        @if (faqItems.length) {
            <!-- FAQ accordion. Native <details> so it needs no JS and is accessible/keyboard-
                 operable out of the box; the answer is staff rich text, sanitised on the way in.
                 The block is #faq; each item's id is a slug of its question (e.g. #faq-do-i-need-a-
                 permit), so a link can open a single item - see applyFragment() in the component. -->
            <div class="page-faq mt-4" id="faq">
                @for (item of faqItems; track item.anchor) {
                    <details class="page-faq__item" [id]="item.anchor" [open]="item.open" (toggle)="onToggle(item, $event)">
                        <summary class="page-faq__q">
                            <span class="page-faq__q-text">{{ item.question }}</span>
                            <!-- Permalink to this question. stopPropagation so it sets the URL
                                 fragment (routerLink) without also toggling the accordion. -->
                            <a
                                class="page-faq__permalink"
                                [routerLink]="[]"
                                [fragment]="item.anchor"
                                (click)="$event.stopPropagation()"
                                title="Link to this question"
                                aria-label="Link to this question"
                            >
                                <i class="fa fa-link" aria-hidden="true"></i>
                            </a>
                        </summary>
                        <div class="page-faq__a" [innerHTML]="item.answer"></div>
                    </details>
                }
            </div>
        }

        @if (page.files?.length) {
            <div class="page-files mt-4">
                <h2 class="h5">Downloads</h2>
                <ul class="list-unstyled mb-0">
                    @for (file of page.files; track file.url) {
                        <li class="mb-1">
                            <a [href]="file.url" target="_blank" rel="noopener"><i class="fa fa-download me-2"></i>{{ file.name }}</a>
                        </li>
                    }
                </ul>
            </div>
        }
    }
</div>
